import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { locationService } from '../services/api';
import { showToast } from '../components/Toaster';
import FavoriteButton from '../components/FavoriteButton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const formatNumberWithSpaces = (num: number): string => {
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
        <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{label}</p>
        <p style={{ margin: 0, color: '#8884d8' }}>
          Vekt: {formatNumberWithSpaces(payload[0].value)} kg
        </p>
      </div>
    );
  }
  return null;
};

function LocationAnalysis() {
  const { locationId } = useParams<{ locationId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [fromDate, setFromDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [selectedSpecies, setSelectedSpecies] = useState<string | null>(null);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [expandedVessels, setExpandedVessels] = useState<Set<string>>(new Set());
  const [locationInfo, setLocationInfo] = useState<any>(location.state);

  // Fetch location info if not available from navigation state (direct URL access)
  useEffect(() => {
    const fetchLocationInfo = async () => {
      if (!locationId || locationInfo) return;

      try {
        const searchResults = await locationService.search(locationId, 1);
        if (searchResults && searchResults.length > 0) {
          setLocationInfo(searchResults[0]);
        } else {
          showToast('Sted ikke funnet', 'error');
          navigate('/');
        }
      } catch (error) {
        showToast('Feil ved lasting av stedsinformasjon', 'error');
        navigate('/');
      }
    };

    fetchLocationInfo();
  }, [locationId]);

  useEffect(() => {
    loadData();
  }, [fromDate, toDate]);

  const loadData = async () => {
    if (!locationId) return;

    setLoading(true);
    try {
      const result = await locationService.getIntake(locationId, fromDate, toDate);
      setData(result);
    } catch (error) {
      showToast('Feil ved lasting av stedsdata. Vennligst prøv igjen.', 'error');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  if (!locationId) {
    return (
      <div className="card">
        <p>Ingen sted valgt. <a href="/">Gå tilbake til søk</a></p>
      </div>
    );
  }

  return (
    <div>
      <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: '0', flex: 1 }}>{locationInfo?.location_name || 'Stedsanalyse'}</h2>
        {locationInfo && (
          <div onClick={(e) => e.stopPropagation()}>
            <FavoriteButton
              type="location"
              id={locationId || ''}
              locationName={locationInfo.location_name}
              locationType={locationInfo.type || 'location'}
              size="medium"
            />
          </div>
        )}
      </div>

      <div className="card">
        <h3 style={{ marginBottom: '20px' }}>Velg datoperiode</h3>
        <div className="form-group date-range-group">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label htmlFor="fromDate">Fra dato</label>
            <input
              id="fromDate"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label htmlFor="toDate">Til dato</label>
            <input
              id="toDate"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Laster stedsdata...</div>
      ) : data ? (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">Totalt mottak (kg)</div>
              <div className="stat-value">{formatNumberWithSpaces(data.total_weight_kg || 0)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Antall registreringer</div>
              <div className="stat-value">{data.daily?.length || 0}</div>
            </div>
          </div>

          {Object.keys(data.species_breakdown || {}).length > 0 && (
            <div className="chart-container">
              <div className="chart-title">Mottak per art {selectedSpecies && `(Filtrert: ${selectedSpecies})`}</div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={Object.entries(data.species_breakdown).map(([species, stats]: [string, any]) => ({
                    species: species.substring(0, 20),
                    fullSpecies: species,
                    weight: stats.weight_kg,
                  }))}
                  onClick={(state: any) => {
                    if (state && state.activeTooltipIndex !== undefined) {
                      const clickedSpecies = Object.entries(data.species_breakdown)[state.activeTooltipIndex]?.[0];
                      if (clickedSpecies) {
                        setSelectedSpecies(selectedSpecies === clickedSpecies ? null : clickedSpecies);
                      }
                    }
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="species" style={{ cursor: 'pointer' }} />
                  <YAxis tickFormatter={(value) => formatNumberWithSpaces(value)} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="weight" fill="#82ca9d" style={{ cursor: 'pointer' }} />
                </BarChart>
              </ResponsiveContainer>
              {selectedSpecies && (
                <div style={{ marginTop: '10px', textAlign: 'center' }}>
                  <button onClick={() => setSelectedSpecies(null)} style={{ padding: '5px 10px', cursor: 'pointer' }}>
                    Fjern artsfilter
                  </button>
                </div>
              )}
            </div>
          )}

          {data.daily && data.daily.length > 0 && (
            <div className="chart-container">
              <div className="chart-title">Månedlig mottaksutvikling</div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={(() => {
                    const filtered = data.daily.filter((record: any) => !selectedSpecies || record.species === selectedSpecies);
                    const monthlyData: { [key: string]: number } = {};

                    // Generate all months in date range
                    const start = new Date(fromDate);
                    const end = new Date(toDate);
                    for (let d = new Date(start); d <= end; d.setMonth(d.getMonth() + 1)) {
                      const monthKey = d.toISOString().slice(0, 7);
                      monthlyData[monthKey] = 0;
                    }

                    // Aggregate by month
                    filtered.forEach((record: any) => {
                      const monthKey = record.date.slice(0, 7);
                      monthlyData[monthKey] = (monthlyData[monthKey] || 0) + (record.weight_kg || 0);
                    });

                    return Object.entries(monthlyData)
                      .sort((a, b) => a[0].localeCompare(b[0]))
                      .map(([month, weight]) => ({
                        month,
                        weight,
                      }));
                  })()}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => formatNumberWithSpaces(value)} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="weight" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="card">
            <h3 style={{ marginBottom: '15px' }}>Daglige mottaksregistreringer {selectedSpecies && `(${selectedSpecies})`}</h3>
            {/* Table Header */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '32px 60px 1fr 50px',
                gap: '8px',
                padding: '8px 10px',
                backgroundColor: '#e8e8e8',
                alignItems: 'center',
                fontSize: '0.85em',
                fontWeight: '700',
                color: '#333',
                borderBottom: '2px solid #999'
              }}
            >
              <div></div>
              <div>Dato</div>
              <div>Fartøy / Art</div>
              <div style={{ textAlign: 'right' }}>Vekt</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
              {(() => {
                const filtered = data.daily.filter((record: any) => !selectedSpecies || record.species === selectedSpecies);
                const grouped = filtered.reduce((acc: any, record: any) => {
                  const date = record.date;
                  if (!acc[date]) {
                    acc[date] = { records: [], weight: 0, value: 0 };
                  }
                  acc[date].records.push(record);
                  acc[date].weight += record.weight_kg || 0;
                  acc[date].value += record.value_nok || 0;
                  return acc;
                }, {});

                const sorted = Object.entries(grouped).sort(([dateA], [dateB]) =>
                  new Date(dateB).getTime() - new Date(dateA).getTime()
                );

                return sorted.map(([date, group]: [string, any]) => {
                  const isDateExpanded = expandedDates.has(date);
                  const toggleDateExpand = () => {
                    const newSet = new Set(expandedDates);
                    if (isDateExpanded) {
                      newSet.delete(date);
                    } else {
                      newSet.add(date);
                    }
                    setExpandedDates(newSet);
                  };

                  // Group vessel within this date
                  const vesselGrouped = group.records.reduce((acc: any, record: any) => {
                    const vesselKey = record.registreringsmerke || 'Unknown';
                    const vesselName = record.vessel_name || 'N/A';
                    if (!acc[vesselKey]) {
                      acc[vesselKey] = { name: vesselName, records: [], weight: 0, value: 0 };
                    }
                    acc[vesselKey].records.push(record);
                    acc[vesselKey].weight += record.weight_kg || 0;
                    acc[vesselKey].value += record.value_nok || 0;
                    return acc;
                  }, {});

                  return (
                    <div key={date} style={{ borderBottom: '1px solid #ddd' }}>
                      {/* Main date row */}
                      <div
                        onClick={toggleDateExpand}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '32px 60px 1fr 50px',
                          gap: '8px',
                          padding: '12px 10px',
                          backgroundColor: '#f5f5f5',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontSize: '0.9em'
                        }}
                      >
                        <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
                          {isDateExpanded ? '▼' : '▶'}
                        </div>
                        <div style={{ fontWeight: '600', whiteSpace: 'nowrap' }}>
                          {date}
                        </div>
                        <div></div>
                        <div style={{ textAlign: 'right', fontWeight: '600' }}>
                          {formatNumberWithSpaces(group.weight)}
                        </div>
                      </div>

                      {/* Vessel level - expanded when date is expanded */}
                      {isDateExpanded && Object.entries(vesselGrouped).map(([vesselKey, vesselData]: [string, any]) => {
                        const vesselRowKey = `${date}-${vesselKey}`;
                        const isVesselExpanded = expandedVessels.has(vesselRowKey);
                        const toggleVesselExpand = () => {
                          const newSet = new Set(expandedVessels);
                          if (isVesselExpanded) {
                            newSet.delete(vesselRowKey);
                          } else {
                            newSet.add(vesselRowKey);
                          }
                          setExpandedVessels(newSet);
                        };

                        const speciesGrouped = vesselData.records.reduce((acc: any, record: any) => {
                          const species = record.species || 'N/A';
                          if (!acc[species]) {
                            acc[species] = { weight: 0, value: 0 };
                          }
                          acc[species].weight += record.weight_kg || 0;
                          acc[species].value += record.value_nok || 0;
                          return acc;
                        }, {});

                        return (
                          <div key={vesselRowKey}>
                            {/* Vessel row */}
                            <div
                              onClick={toggleVesselExpand}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '32px 1fr 50px',
                                gap: '8px',
                                padding: '10px 10px 10px 42px',
                                backgroundColor: '#fafafa',
                                alignItems: 'center',
                                cursor: 'pointer',
                                fontSize: '0.9em',
                                fontWeight: '500',
                                borderTop: '1px solid #eee'
                              }}
                            >
                              <div style={{ textAlign: 'center' }}>
                                {isVesselExpanded ? '▼' : '▶'}
                              </div>
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${vesselKey} - ${vesselData.name}`}>
                                {vesselKey} - {vesselData.name}
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                {formatNumberWithSpaces(vesselData.weight)}
                              </div>
                            </div>

                            {/* Species level - expanded when vessel is expanded */}
                            {isVesselExpanded && Object.entries(speciesGrouped).map(([species, stats]: [string, any]) => (
                              <div
                                key={species}
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: '32px 1fr 50px',
                                  gap: '8px',
                                  padding: '8px 10px 8px 74px',
                                  backgroundColor: '#fcfcfc',
                                  alignItems: 'center',
                                  fontSize: '0.8em',
                                  color: '#666',
                                  borderTop: '1px solid #f0f0f0'
                                }}
                              >
                                <div></div>
                                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {species}
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  {formatNumberWithSpaces(stats.weight)}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </>
      ) : (
        <div className="error">Ingen data tilgjengelig for valgt datoperiode.</div>
      )}
    </div>
  );
}

export default LocationAnalysis;
